import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from "react-i18next";
import Button from "@mui/material/Button";

import InfiniteCarousel from "../components/shared/InfiniteCarousel";
import Tags, { tags } from "../components/shared/Tags";

import WR1 from '../images/projects/WolfRun/thumbnail.png';
import WR2 from '../images/projects/WolfRun/Lf3Wnz.png'
import WR3 from '../images/projects/WolfRun/sbXTqs.png'
import WR4 from '../images/projects/WolfRun/SRZKho.png'
import WR5 from '../images/projects/WolfRun/Rv0U46.png'
import WR6 from '../images/projects/WolfRun/SPZvkf.png'

import IB1 from '../images/projects/InBattle/lucid1.jpg'
import IB2 from '../images/projects/InBattle/lucid2.jpg'
import IB3 from '../images/projects/InBattle/lucid3.jpg'
import IB4 from '../images/projects/InBattle/lucid4.jpg'
import IB5 from '../images/projects/InBattle/lucid5.jpg'

import ToZ1 from '../images/projects/ToZ/toz1.png'
import ToZ2 from '../images/projects/ToZ/toz2.png'
import ToZ3 from '../images/projects/ToZ/toz3.png'
import ToZ4 from '../images/projects/ToZ/toz4.png'
import ToZ5 from '../images/projects/ToZ/toz5.png'
import ToZ6 from '../images/projects/ToZ/toz6.png'
import ToZ7 from '../images/projects/ToZ/toz7.png'
import ToZ8 from '../images/projects/ToZ/toz8.png'
import ToZ9 from '../images/projects/ToZ/toz9.png'
import ToZ10 from '../images/projects/ToZ/toz10.png'
import ToZ11 from '../images/projects/ToZ/toz11.png'

import TS1 from '../images/projects/TS/playme1.png'
import TS2 from '../images/projects/TS/playme2.png'
import TS3 from '../images/projects/TS/playme3.png'
import TS4 from '../images/projects/TS/playme4.png'

import S31 from "../images/projects/S3/Screenshot_20260402_165602.png"
import S32 from "../images/projects/S3/Screenshot_20260402_170107.png"
import S33 from "../images/projects/S3/Screenshot_20260402_165403.png"
import S34 from "../images/projects/S3/Screenshot_20260402_170030.png"

const ProjectCard = ({ project }) => {
    const [activeImage, setActiveImage] = useState(0);
    const ignoreNextClickRef = useRef(false);
    const { t } = useTranslation();

    useEffect(() => {
        setActiveImage(0);
    }, [project]);

    const nextImage = () => {
        setActiveImage((prev) => (prev + 1) % project.images.length);
    };

    const handleImageTap = (event) => {
        event.stopPropagation();

        if (ignoreNextClickRef.current) {
            ignoreNextClickRef.current = false;
            return;
        }

        nextImage();
    };

    const handleImageTouchEnd = (event) => {
        event.stopPropagation();
        ignoreNextClickRef.current = true;
        nextImage();
    };

    return (
        <article className="project-card">
            <div className="project-card-media">
                <div
                    className="project-image-carousel"
                    onClick={handleImageTap}
                    onTouchEnd={handleImageTouchEnd}
                    role="button"
                    tabIndex={0}
                    aria-label="Project images carousel"
                >
                    <img
                        className="project-card-image"
                        src={project.images[activeImage]}
                        alt={`${project.title} screenshot ${activeImage + 1}`}
                    />
                </div>

                <div className="project-image-dots" aria-label="Image carousel indicators">
                    {project.images.map((_, index) => (
                        <button
                            key={index}
                            type="button"
                            className={`project-image-dot ${index === activeImage ? 'active' : ''}`}
                            onClick={(event) => {
                                event.stopPropagation();
                                setActiveImage(index);
                            }}
                            aria-label={`Go to image ${index + 1}`}
                        />
                    ))}
                </div>
            </div>

            <div className="project-card-content">
                <div>
                    <h2 className="project-card-title">{project.title}</h2>
                    <div className={'project-card-companies'}>
                        <p className="project-card-studio">{project.studio}</p>
                        {project.studio && project.publisher && <p>•</p>}
                        <p className="project-card-publisher">{project.publisher}</p>
                    </div>

                    <Tags tags={project.tags} />

                    <div className="project-card-contributions">
                        <ul>
                            {project.contributions.map((item) => (
                                <li key={item}>{item}</li>
                            ))}
                        </ul>
                    </div>
                </div>

                <div className="project-card-footer">
                    <Button
                        color="info"
                        variant="contained"
                        className="project-card-link"
                        onClick={() => window.open(project.link, '_blank', 'noopener,noreferrer')}
                    >
                        {t('projectLink')}
                    </Button>
                </div>
            </div>
        </article>
    );
};

const Projects = ({ isActive = false }) => {
    const { t, i18n } = useTranslation();
    const [showSwipeHint, setShowSwipeHint] = useState(false);
    const hintDismissedRef = useRef(false);

    const projects = useMemo(() => ([
        {
            images: [WR1, WR2, WR3, WR4, WR5, WR6],
            title: 'Wolf Run',
            studio: '',
            publisher: '',
            tags: [tags.unity, tags.csharp],
            contributions: [
                t('WRContribution2'),
                'UI',
                t('WRContribution3'),
                t('WRContribution4'),
                t('WRContribution5'),
            ],
            link: 'https://barracius1.itch.io/wolf-run',
        },
        {
            images: [IB1, IB2, IB3, IB4, IB5],
            title: 'Champions Warfare',
            studio: 'InBattle',
            publisher: '',
            tags: [tags.unreal, tags.cpp, tags.blueprint],
            contributions: [
                t('IBContribution2'),
                t('IBContribution3'),
                t('IBContribution4')
            ],
            link: i18n.language === 'en' ?
                'https://www.inbattle.com/inbattle_player/inbattlelanding/' :
                'https://luciddreams.cl/experiencias/inbattle-champions-warfare/',
        },
        {
            images: [ToZ1, ToZ2, ToZ3, ToZ4, ToZ5, ToZ6, ToZ7, ToZ8, ToZ9, ToZ10, ToZ11],
            title: 'Town of Zoz',
            studio: 'Studio Pixanoh',
            publisher: 'Balor Games',
            tags: [tags.unreal, tags.cpp, tags.blueprint],
            contributions: [
                t('ToZContribution2'),
                t('ToZContribution3'),
                t('ToZContribution4'),
            ],
            link: 'https://store.steampowered.com/app/1748110/Town_of_Zoz/',
        },
        {
            images: [TS3, TS1, TS2, TS4],
            title: 'The Simulation',
            studio: 'PlayMeStudio',
            publisher: 'Blumhouse Games',
            tags: [tags.unreal, tags.cpp, tags.blueprint],
            contributions: [
                t('TSContribution2'),
                t('TSContribution3'),
                t('TSContribution4')
            ],
            link: 'https://store.steampowered.com/app/3394500/The_Simulation/',
        },
        {
            images: [S31, S32, S33, S34],
            title: 'Shenmue III Enhanced',
            studio: 'YS NET | Sneakybox',
            publisher: 'ININ',
            tags: [tags.unreal, tags.cpp, tags.blueprint],
            contributions: [
                t('S3Contribution2'),
                t('S3Contribution3'),
                t('S3Contribution4')
            ],
            link: 'https://store.steampowered.com/app/3957530/Shenmue_III_Enhanced/',
        }
    ]), [i18n.language]);

    useEffect(() => {
        const isMobile = window.matchMedia('(max-width: 767px)').matches;
        if (!isMobile || !isActive || hintDismissedRef.current) return;

        setShowSwipeHint(true);
    }, [isActive]);

    useEffect(() => {
        if (!showSwipeHint) return;

        const dismiss = () => {
            hintDismissedRef.current = true;
            setShowSwipeHint(false);
        };

        window.addEventListener('pointerdown', dismiss, { once: true });
        return () => window.removeEventListener('pointerdown', dismiss);
    }, [showSwipeHint]);

    return (
        <div className="projects-wrapper">
            <InfiniteCarousel
                items={projects}
                className="projects"
                itemClassName="carousel-card project"
                prevAriaLabel="Previous project"
                nextAriaLabel="Next project"
                renderItem={(project) => <ProjectCard project={project} />}
            />

            {showSwipeHint && (
                <div className="projects-swipe-hint" aria-hidden="true">
                    <div className="projects-swipe-hint-card">
                        <div className="swipe-finger" />
                        <p>{t('swipeTip')}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Projects;